import Swiper from 'swiper';
import { EffectFade, FreeMode, Mousewheel, Navigation } from 'swiper/modules';
import 'swiper/css/bundle';

const HIDDEN_CLASS = 'jc-hidden';
const BODY_LOCK_CLASS = 'jc-overflow-hidden';

const getGroups = (modal) => [...modal.querySelectorAll('[data-product-story-group]')];
const getActiveGroup = (modal) => modal.querySelector('.swiper-slide-active [data-product-story-group]');
const getVideos = (group) => (group ? [...group.querySelectorAll('[data-story-video-item] video')] : []);
const getCurrentVideoIndex = (group) => Number(group?.dataset.currentVideoIndex || 0);
const getCurrentVideo = (group) => getVideos(group)[getCurrentVideoIndex(group)];

const updateThumbFades = (wrapper) => {
  if (!wrapper) return;

  const thumbs = wrapper.querySelector('.js-main-product-stories-thumbs');
  const swiper = thumbs?.swiper;
  const leftFade = wrapper.querySelector('[data-product-stories-fade-left]');
  const rightFade = wrapper.querySelector('[data-product-stories-fade-right]');
  const isLocked = !swiper || swiper.isLocked;

  leftFade?.classList.toggle(HIDDEN_CLASS, isLocked || swiper.isBeginning);
  rightFade?.classList.toggle(HIDDEN_CLASS, isLocked || swiper.isEnd);
};

const updatePreviousStoryControls = (modal) => {
  if (!modal) return;

  const swiper = modal.querySelector('[data-product-stories-modal-swiper]')?.swiper;
  const activeGroup = getActiveGroup(modal);
  const isFirstStory = (swiper?.activeIndex || 0) === 0 && getCurrentVideoIndex(activeGroup) === 0;

  modal.querySelectorAll('[data-product-story-button-prev]').forEach((button) => {
    button.classList.toggle(HIDDEN_CLASS, isFirstStory);
  });
};

const setPlaybackControls = (group, isPlaying) => {
  const playButton = group?.querySelector('[data-product-stories-play]');
  const toggleButton = group?.querySelector('[data-product-stories-toggle]');

  if (playButton) {
    playButton.classList.toggle('jc-opacity-0', isPlaying);
    playButton.classList.toggle('jc-opacity-100', !isPlaying);
    playButton.classList.toggle('jc-pointer-events-none', isPlaying);
    playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
  }

  if (toggleButton) {
    toggleButton.dataset.state = isPlaying ? 'playing' : 'paused';
    toggleButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
  }
};

const updateTimeline = (group, activeIndex = 0, progress = 0) => {
  if (!group) return;

  group.querySelectorAll('[data-story-segment]').forEach((segment, index) => {
    const progressBar = segment.querySelector('[data-story-segment-progress]');
    if (!progressBar) return;

    if (index < activeIndex) {
      progressBar.style.width = '100%';
    } else if (index === activeIndex) {
      progressBar.style.width = `${progress}%`;
    } else {
      progressBar.style.width = '0%';
    }
  });
};

const hideVideoItems = (group) => {
  if (!group) return;

  group.querySelectorAll('[data-story-video-item]').forEach((item) => item.classList.add(HIDDEN_CLASS));
};

const pauseVideo = (video, reset = true) => {
  if (!video) return;

  video.pause();
  if (reset) video.currentTime = 0;
};

const pauseGroup = (group, reset = true) => {
  if (!group) return;

  getVideos(group).forEach((video) => pauseVideo(video, reset));
  setPlaybackControls(group, false);

  if (reset) {
    group.dataset.currentVideoIndex = '0';
    hideVideoItems(group);
    group.querySelector('[data-story-video-item]')?.classList.remove(HIDDEN_CLASS);
    updateTimeline(group, 0, 0);
  }
};

const pauseAllGroups = (modal, reset = true) => {
  getGroups(modal).forEach((group) => pauseGroup(group, reset));
};

const showVideoAtIndex = (group, index) => {
  if (!group) return null;

  const items = [...group.querySelectorAll('[data-story-video-item]')];
  const item = items[index];
  if (!item) return null;

  hideVideoItems(group);
  item.classList.remove(HIDDEN_CLASS);
  group.dataset.currentVideoIndex = String(index);
  updateTimeline(group, index, 0);

  return item.querySelector('video');
};

const playCurrentVideo = (group) => {
  const video = getCurrentVideo(group) || showVideoAtIndex(group, 0);
  if (!video) return;

  updatePreviousStoryControls(group.closest('[data-product-stories-modal]'));

  video.muted = group.closest('[data-product-stories-modal]')?.dataset.muted === 'true';
  video.play()
    .then(() => setPlaybackControls(group, true))
    .catch(() => setPlaybackControls(group, false));
};

const playVideoAtIndex = (group, index) => {
  const video = showVideoAtIndex(group, index);
  if (!video) return;

  updatePreviousStoryControls(group.closest('[data-product-stories-modal]'));

  video.muted = group.closest('[data-product-stories-modal]')?.dataset.muted === 'true';
  video.currentTime = 0;
  video.play()
    .then(() => setPlaybackControls(group, true))
    .catch(() => setPlaybackControls(group, false));
};

const toggleCurrentVideo = (group) => {
  const video = getCurrentVideo(group);
  if (!video) return;

  if (video.paused) {
    playCurrentVideo(group);
    return;
  }

  video.pause();
  setPlaybackControls(group, false);
};

const navigateGroup = (modal, direction) => {
  const swiper = modal.querySelector('[data-product-stories-modal-swiper]')?.swiper;
  if (!swiper) return;

  if (direction === 'next') {
    if (swiper.isEnd) {
      closeStoriesModal(modal);
      return;
    }

    swiper.slideNext();
    return;
  }

  if (swiper.isBeginning) return;
  swiper.slidePrev();
};

const navigateStory = (modal, direction) => {
  const group = getActiveGroup(modal);
  if (!group) return;

  const videos = getVideos(group);
  const currentIndex = getCurrentVideoIndex(group);
  const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

  pauseVideo(videos[currentIndex]);

  if (nextIndex >= 0 && nextIndex < videos.length) {
    playVideoAtIndex(group, nextIndex);
    return;
  }

  navigateGroup(modal, direction);
};

function closeStoriesModal(modal) {
  pauseAllGroups(modal, true);
  modal.classList.add(HIDDEN_CLASS);
  modal.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(BODY_LOCK_CLASS);
  document.body.classList.remove(BODY_LOCK_CLASS);
}

const openStoriesModal = (modal, index) => {
  modal.classList.remove(HIDDEN_CLASS);
  modal.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add(BODY_LOCK_CLASS);
  document.body.classList.add(BODY_LOCK_CLASS);

  const swiper = initModalSwiper(modal);
  swiper?.update();
  swiper?.slideTo(index, 0);

  pauseAllGroups(modal, true);
  updatePreviousStoryControls(modal);
  requestAnimationFrame(() => playVideoAtIndex(getActiveGroup(modal), 0));
};

const bindGroupVideos = (modal) => {
  getGroups(modal).forEach((group) => {
    if (group.dataset.productStoryBound === 'true') return;

    getVideos(group).forEach((video, index) => {
      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        updateTimeline(group, index, Math.min((video.currentTime / video.duration) * 100, 100));
      });

      video.addEventListener('play', () => setPlaybackControls(group, true));
      video.addEventListener('pause', () => setPlaybackControls(group, false));
      video.addEventListener('ended', () => navigateStory(modal, 'next'));
    });

    group.querySelector('[data-product-stories-play]')?.addEventListener('click', () => toggleCurrentVideo(group));
    group.querySelector('[data-product-stories-toggle]')?.addEventListener('click', () => toggleCurrentVideo(group));
    group.querySelector('[data-product-story-prev]')?.addEventListener('click', () => navigateStory(modal, 'prev'));
    group.querySelector('[data-product-story-next]')?.addEventListener('click', () => navigateStory(modal, 'next'));

    group.dataset.productStoryBound = 'true';
  });
};

const bindModalControls = (modal) => {
  if (modal.dataset.productStoriesControlsBound === 'true') return;

  modal.dataset.muted = 'false';

  modal.addEventListener('click', (event) => {
    if (event.target === modal || event.target.matches('[data-product-stories-backdrop]')) {
      closeStoriesModal(modal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains(HIDDEN_CLASS)) closeStoriesModal(modal);
  });

  modal.querySelectorAll('[data-product-stories-close]').forEach((button) => {
    button.addEventListener('click', () => closeStoriesModal(modal));
  });

  modal.querySelectorAll('[data-product-stories-mute]').forEach((button) => {
    button.addEventListener('click', () => {
      const shouldMute = modal.dataset.muted !== 'true';
      modal.dataset.muted = shouldMute ? 'true' : 'false';

      modal.querySelectorAll('video').forEach((video) => {
        video.muted = shouldMute;
      });

      modal.querySelectorAll('[data-product-stories-mute]').forEach((muteButton) => {
        muteButton.dataset.state = shouldMute ? 'mute' : 'unmute';
      });
    });
  });

  modal.querySelectorAll('[data-product-story-button-prev]').forEach((button) => {
    button.addEventListener('click', () => navigateStory(modal, 'prev'));
  });

  modal.querySelectorAll('[data-product-story-button-next]').forEach((button) => {
    button.addEventListener('click', () => navigateStory(modal, 'next'));
  });

  modal.dataset.productStoriesControlsBound = 'true';
};

function initModalSwiper(modal) {
  const modalSwiperElement = modal.querySelector('[data-product-stories-modal-swiper]');
  if (!modalSwiperElement) return null;
  if (modalSwiperElement.swiper) return modalSwiperElement.swiper;

  return new Swiper(modalSwiperElement, {
    modules: [Navigation, Mousewheel, EffectFade],
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    mousewheel: {
      forceToAxis: true,
    },
    navigation: {
      nextEl: modalSwiperElement.querySelector('.swiper-button-next'),
      prevEl: modalSwiperElement.querySelector('.swiper-button-prev'),
    },
    observer: true,
    observeParents: true,
    on: {
      slideChange() {
        pauseAllGroups(modal, true);
        updatePreviousStoryControls(modal);
        requestAnimationFrame(() => playVideoAtIndex(getActiveGroup(modal), 0));
      },
    },
  });
}

export const initMainProductStories = () => {
  document.querySelectorAll('[data-main-product-stories]').forEach((wrapper) => {
    const thumbs = wrapper.querySelector('.js-main-product-stories-thumbs');
    if (thumbs && !thumbs.swiper) {
      new Swiper(thumbs, {
        modules: [FreeMode],
        slidesPerView: 'auto',
        spaceBetween: 12,
        freeMode: true,
        watchOverflow: true,
        on: {
          init() {
            updateThumbFades(wrapper);
          },
          progress() {
            updateThumbFades(wrapper);
          },
          resize() {
            updateThumbFades(wrapper);
          },
          update() {
            updateThumbFades(wrapper);
          },
        },
      });
    }
    updateThumbFades(wrapper);

    const modal = document.querySelector('[data-product-stories-modal]');
    if (!modal) return;

    bindGroupVideos(modal);
    bindModalControls(modal);

    wrapper.querySelectorAll('[data-product-story-open]').forEach((trigger) => {
      if (trigger.dataset.productStoryBound === 'true') return;

      trigger.addEventListener('click', () => {
        openStoriesModal(modal, Number(trigger.dataset.activeSlide || 0));
      });

      trigger.dataset.productStoryBound = 'true';
    });
  });
};
